import React, { memo, useState, useMemo } from 'react';
import { cn } from '@/lib/utils';
import { UniversalMap, MapMarker } from '@/components/maps';
import { MarketplaceListingCard } from './MarketplaceListingCard';
import { normalizeListingForCard } from '@/utils/marketplaceCardNormalizer';
import { Pagination } from '../Pagination';
import EmptyState from '../EmptyState';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import { Map as MapIcon, ChevronUp, ChevronDown, List as ListIcon } from 'lucide-react';
import { MobileListView } from './MobileListView';
import { MobileMapPreviewCard } from './MobileMapPreviewCard';
import { CategoryDivider } from './CategoryDivider';

interface MarketplaceViewportProps {
  // View config
  viewMode: 'grid' | 'list' | 'map';
  assetType: 'helicopter' | 'engine' | 'parts' | 'jobs' | 'services';
  isMobile: boolean;
  isTablet: boolean;
  markerDisplayMode: 'icon' | 'hero';

  // Data
  effectiveListings: any[];
  effectiveLoading: boolean;
  sortedListingsForDisplay: any[];
  groupedListings: [string, any[]][];
  listingsWithCoordinates: MapMarker[];
  totalCount: number;

  // Pagination
  currentPage: number;
  itemsPerPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;

  // Selection
  selectedMapListingId: string | null;
  buyRequestType: string | null;

  // Filters
  activeFiltersCount: number;
  searchInput: string;

  // Handlers (must be stable via useCallback)
  handleListingClick: (listing: any) => void;
  handleMapListingSelect: (id: string) => void;
  handleMapReady: () => void;
  handleBoundsChanged: (bounds: { north: number; south: number; east: number; west: number }) => void;
  onClearFilters: () => void;
  setSelectedMapListingId: (id: string | null) => void;
  getGroupedByType: (listings: any[], page: number, perPage: number) => [string, any[]][];
  buildMobileListingProps: (listing: any) => any;
  buildPreviewCardProps: (listing: any) => any;
}

const MarketplaceViewport: React.FC<MarketplaceViewportProps> = memo(({
  viewMode,
  assetType,
  isMobile,
  isTablet,
  markerDisplayMode,
  effectiveListings,
  effectiveLoading,
  sortedListingsForDisplay,
  groupedListings,
  listingsWithCoordinates,
  totalCount,
  currentPage,
  itemsPerPage,
  totalPages,
  onPageChange,
  selectedMapListingId,
  buyRequestType,
  activeFiltersCount,
  searchInput,
  handleListingClick,
  handleMapListingSelect,
  handleMapReady,
  handleBoundsChanged,
  onClearFilters,
  setSelectedMapListingId,
  getGroupedByType,
  buildMobileListingProps,
  buildPreviewCardProps,
}) => {
  const [mapCollapsed, setMapCollapsed] = useState<boolean>(() => isMobile);

  if (viewMode === 'map') {
    return (
      <>
        {/* Desktop: Two-card layout - Listings card + Map card (desktop only, not tablet) */}
        {!isMobile && !isTablet && (
          <div className="relative w-full z-30 px-6 py-4 h-[calc(100vh-180px)]">
            <div className="max-w-[2200px] mx-auto flex gap-4 h-full">
              {/* Left Card - Scrollable Listings */}
              <div className={cn(
                "bg-card rounded-2xl border shadow-sm overflow-hidden flex flex-col h-full",
                isTablet ? "flex-none w-[380px] max-w-[380px]" : "flex-1 max-w-[55%]"
              )} data-tutorial-id="marketplace-listing-cards">
                <div className="px-5 py-4 border-b bg-muted/30 flex-shrink-0">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-sm">
                      {totalCount} Listing{totalCount !== 1 ? 's' : ''}
                    </h3>
                    <Badge variant="outline" className="text-xs">
                      {assetType === 'helicopter' ? 'Helicopters' : assetType === 'engine' ? 'Engines' : assetType === 'jobs' ? 'Jobs' : assetType === 'services' ? 'Services' : 'Parts & Tools'}
                    </Badge>
                  </div>
                </div>
                <ScrollArea className="flex-1 min-h-0">
                  <div className="p-6">
                    <div
                      className="grid gap-5"
                      style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))' }}
                    >
                      {(() => {
                        const sortedGroups = getGroupedByType(sortedListingsForDisplay, currentPage, itemsPerPage);
                        let isFirstGroup = true;
                        return (
                          <>
                            {sortedGroups.map(([listingType, listings]) => {
                              const dividerClass = isFirstGroup ? "" : "mt-4";
                              isFirstGroup = false;
                              return (
                                <React.Fragment key={listingType}>
                                  <CategoryDivider
                                    listingType={listingType}
                                    count={listings.length}
                                    assetType={assetType}
                                    isBuyRequest={assetType === 'helicopter' && listingType.includes('buy')}
                                    className={dividerClass}
                                  />
                                  {listings.map((listing) => {
                                    const isSelected = selectedMapListingId === listing.id;
                                    return (
                                      <div
                                        key={listing.id}
                                        id={`listing-card-${listing.id}`}
                                        className={cn(
                                          "relative transition-all duration-300 rounded-xl w-full",
                                          isSelected && [
                                            "z-20",
                                            "ring-2 ring-[hsl(45,93%,47%)]",
                                            "ring-offset-4 ring-offset-background",
                                            "shadow-xl shadow-[hsl(45,93%,47%)]/30",
                                          ]
                                        )}
                                      >
                                        <MarketplaceListingCard
                                          {...normalizeListingForCard(listing, assetType, !!buyRequestType)}
                                          onClick={() => handleListingClick(listing)}
                                          onViewDetails={() => handleListingClick(listing)}
                                          isSelected={isSelected}
                                        />
                                      </div>
                                    );
                                  })}
                                </React.Fragment>
                              );
                            })}
                          </>
                        );
                      })()}
                    </div>
                    {totalPages > 1 && (
                      <div className="mt-6 pb-4">
                        <Pagination
                          currentPage={currentPage}
                          totalPages={totalPages}
                          onPageChange={onPageChange}
                          itemsPerPage={itemsPerPage}
                          totalItems={totalPages * itemsPerPage}
                        />
                      </div>
                    )}
                  </div>
                </ScrollArea>
              </div>

              {/* Right Card - Square Map */}
              <div className={cn(
                "flex-1 bg-card rounded-2xl border shadow-sm overflow-hidden flex flex-col h-full",
                isTablet ? "min-w-0" : "min-w-[500px]"
              )} data-tutorial-id="marketplace-map">
                <div className="px-5 py-3 border-b bg-muted/30 flex-shrink-0">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-sm flex items-center gap-2">
                      <MapIcon className="w-4 h-4" />
                      Map View
                    </h3>
                    <Badge variant="outline" className="text-xs">
                      {listingsWithCoordinates.length} on map
                    </Badge>
                  </div>
                </div>
                <div className="flex-1 min-h-0 p-3">
                  <UniversalMap
                    markers={listingsWithCoordinates}
                    selectedMarkerId={selectedMapListingId}
                    onMarkerClick={handleMapListingSelect}
                    onMapReady={handleMapReady}
                    onBoundsChanged={handleBoundsChanged}
                    height="h-full"
                    className="w-full h-full rounded-xl"
                    assetType={assetType}
                    mode="interactive"
                    markerDisplayMode={markerDisplayMode}
                    onViewDetailsClick={(id) => {
                      const listing = effectiveListings.find(l => l.id === id);
                      if (listing) handleListingClick(listing);
                    }}
                  />
                </div>
                {listingsWithCoordinates.length === 0 && !effectiveLoading && (
                  <div className="absolute top-4 left-1/2 -translate-x-1/2 z-10 bg-background/95 backdrop-blur-sm px-4 py-2 rounded-full shadow-lg border">
                    <span className="text-sm text-muted-foreground">No listings with location data found</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Mobile & Tablet: Stacked layout */}
        {(isMobile || isTablet) && (
          <div className={cn(
            "flex flex-col z-30",
            isMobile && cn("fixed inset-x-0 bottom-[var(--bottom-nav-offset)]", (assetType === 'jobs' || assetType === 'services') ? "top-[calc(140px_+_env(safe-area-inset-top,0px)_+_8px)]" : "top-[calc(112px_+_env(safe-area-inset-top,0px)_+_8px)]"),
            isTablet && "relative w-full h-[calc(100vh-120px)] px-4 py-4"
          )}>
            {/* Map section - collapsible */}
            <div className={cn(
              "flex-shrink-0 relative transition-[height] duration-300",
              mapCollapsed
                ? "h-0 overflow-hidden"
                : isMobile ? "h-[55%]" : "h-[50%] rounded-2xl overflow-hidden border shadow-sm"
            )}>
              {listingsWithCoordinates.length > 0 ? (
                <>
                  <UniversalMap
                    markers={listingsWithCoordinates}
                    selectedMarkerId={selectedMapListingId}
                    onMarkerClick={handleMapListingSelect}
                    onMapReady={handleMapReady}
                    height="h-full"
                    assetType={assetType}
                    mode="interactive"
                    markerDisplayMode={markerDisplayMode}
                    showInfoWindow={false}
                    panToSelectedMarker={true}
                    onViewDetailsClick={(id) => {
                      const listing = effectiveListings.find(l => l.id === id);
                      if (listing) handleListingClick(listing);
                    }}
                  />
                  {/* Mobile Preview Card Overlay */}
                  {selectedMapListingId && (() => {
                    const sel = effectiveListings.find(l => l.id === selectedMapListingId);
                    if (!sel) return null;
                    return (
                      <MobileMapPreviewCard
                        listing={buildPreviewCardProps(sel)}
                        onViewDetails={() => handleListingClick(sel)}
                        onClose={() => setSelectedMapListingId(null)}
                      />
                    );
                  })()}
                </>
              ) : (
                <div className="flex items-center justify-center h-full bg-muted/20">
                  <EmptyState type="filtered" hasFilters={false} assetType={assetType} />
                </div>
              )}
            </div>

            {/* Listings section */}
            <div className={cn(
              "flex-1 min-h-0 overflow-hidden bg-background flex flex-col",
              isMobile ? "border-t" : "mt-4 rounded-2xl border shadow-sm"
            )}>
              <div className="flex-shrink-0 sticky top-0 z-30 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 border-b border-border/50 px-3 py-2">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs font-semibold text-foreground">
                    {totalCount} listing{totalCount !== 1 ? 's' : ''} found
                  </span>
                  {!isMobile && (
                    <button
                      type="button"
                      onClick={() => setMapCollapsed(v => !v)}
                      aria-label={mapCollapsed ? "Expand map" : "Collapse map"}
                      data-tutorial-id="map-collapse-toggle"
                      className={cn(
                        "inline-flex h-9 min-w-[112px] items-center justify-center gap-1.5 rounded-full border px-3 text-[11px] font-semibold shadow-sm transition-colors",
                        mapCollapsed
                          ? "bg-primary text-primary-foreground border-primary hover:bg-primary/90"
                          : "bg-background text-foreground border-border hover:bg-muted"
                      )}
                    >
                      <MapIcon className="w-3.5 h-3.5" />
                      {mapCollapsed ? 'Show map' : 'Hide map'}
                      {mapCollapsed ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronUp className="w-3.5 h-3.5" />}
                    </button>
                  )}
                </div>
              </div>
              <div className="flex-1 min-h-0 overflow-y-auto px-3 pb-3 pt-2">
                {(() => {
                  // Re-group the current page slice so category dividers stay accurate per page
                  const pageListings = sortedListingsForDisplay.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
                  const groupsMap = new Map<string, any[]>();
                  pageListings.forEach((l) => {
                    const key = l.listing_type || l.listingType || 'other';
                    if (!groupsMap.has(key)) groupsMap.set(key, []);
                    groupsMap.get(key)!.push(l);
                  });
                  const groups = Array.from(groupsMap.entries());
                  return groups.map(([listingType, listings], groupIndex) => (
                    <React.Fragment key={listingType}>
                      <CategoryDivider
                        listingType={listingType}
                        count={listings.length}
                        assetType={assetType}
                        isBuyRequest={assetType === 'helicopter' && listingType.includes('buy')}
                        className={groupIndex > 0 ? "mt-3" : ""}
                      />
                      <div className={cn("grid gap-2.5 mt-2", isMobile ? "grid-cols-2" : "grid-cols-3")}>
                        {listings.map((listing) => (
                          <MarketplaceListingCard
                            key={listing.id}
                            {...normalizeListingForCard(listing, assetType, !!buyRequestType)}
                            listView={false}
                            mobileCompact={true}
                            isSelected={selectedMapListingId === listing.id}
                            onClick={() => {
                              if (selectedMapListingId === listing.id) {
                                handleListingClick(listing);
                              } else {
                                setSelectedMapListingId(listing.id);
                              }
                            }}
                            onViewDetails={() => handleListingClick(listing)}
                          />
                        ))}
                      </div>
                    </React.Fragment>
                  ));
                })()}
                {totalPages > 1 && (
                  <div className="mt-3 pb-2">
                    <Pagination
                      currentPage={currentPage}
                      totalPages={totalPages}
                      onPageChange={onPageChange}
                      itemsPerPage={itemsPerPage}
                      totalItems={totalPages * itemsPerPage}
                      compact
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Airbnb-style floating Map/List toggle (mobile only) */}
            {isMobile && (
              <button
                type="button"
                onClick={() => setMapCollapsed(v => !v)}
                aria-label={mapCollapsed ? "Show map" : "Show list"}
                data-tutorial-id="map-collapse-toggle"
                className={cn(
                  "fixed right-4 z-40 inline-flex h-11 items-center justify-center gap-2 rounded-full px-5 text-sm font-semibold shadow-xl transition-colors",
                  "bg-foreground text-background hover:bg-foreground/90"
                )}
                style={{ bottom: 'calc(var(--bottom-nav-offset, 0px) + 16px)' }}
              >
                {mapCollapsed ? (
                  <>
                    <MapIcon className="w-4 h-4" />
                    Map
                  </>
                ) : (
                  <>
                    <ListIcon className="w-4 h-4" />
                    List
                  </>
                )}
              </button>
            )}
          </div>
        )}
      </>
    );
  }

  // Grid/List view
  return (
    <div className="max-w-[2200px] mx-auto px-3 md:px-6 py-2 md:py-8 pb-4 md:pb-8" data-tutorial-id="marketplace-listing-cards">
      {groupedListings.length === 0 ? (
        <EmptyState
          type="no-results"
          hasFilters={activeFiltersCount > 0 || searchInput.length > 0}
          onClearFilters={onClearFilters}
          assetType={assetType}
        />
      ) : viewMode === 'list' ? (
        <div className="space-y-2 md:space-y-4">
          {groupedListings.map(([listingType, listings], groupIndex) => (
            <React.Fragment key={listingType}>
              <CategoryDivider
                listingType={listingType}
                count={listings.length}
                assetType={assetType}
                isBuyRequest={assetType === 'helicopter' && listingType.includes('buy')}
                className={groupIndex > 0 ? "mt-6" : ""}
              />
              {listings.map((listing) => (
                isMobile ? (
                  <MobileListView
                    key={listing.id}
                    listing={buildMobileListingProps(listing)}
                    onClick={() => handleListingClick(listing)}
                  />
                ) : (
                  <MarketplaceListingCard
                    key={listing.id}
                    {...normalizeListingForCard(listing, assetType, !!buyRequestType)}
                    listView={true}
                    onClick={() => handleListingClick(listing)}
                    onViewDetails={() => handleListingClick(listing)}
                  />
                )
              ))}
            </React.Fragment>
          ))}
        </div>
      ) : (
        <div className="space-y-4">
          {groupedListings.map(([listingType, listings], groupIndex) => (
            <React.Fragment key={listingType}>
              <CategoryDivider
                listingType={listingType}
                count={listings.length}
                assetType={assetType}
                isBuyRequest={assetType === 'helicopter' && listingType.includes('buy')}
                className={groupIndex > 0 ? "mt-4" : ""}
              />
              <div
                className={cn(
                  "gap-2 md:gap-6",
                  isMobile ? "grid grid-cols-2" : isTablet ? "grid grid-cols-3" : "grid"
                )}
                style={!isMobile && !isTablet ? { gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))' } : undefined}
              >
                {listings.map((listing) => (
                  <MarketplaceListingCard
                    key={listing.id}
                    {...normalizeListingForCard(listing, assetType, !!buyRequestType)}
                    listView={false}
                    mobileCompact={isMobile || isTablet}
                    onClick={() => handleListingClick(listing)}
                    onViewDetails={() => handleListingClick(listing)}
                  />
                ))}
              </div>
            </React.Fragment>
          ))}
        </div>
      )}

      {totalPages > 1 && (
        <div className="mt-3 md:mt-8 flex justify-center px-2 md:px-0">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={onPageChange}
            itemsPerPage={itemsPerPage}
            totalItems={totalPages * itemsPerPage}
            compact={isMobile}
          />
        </div>
      )}
    </div>
  );
});

MarketplaceViewport.displayName = 'MarketplaceViewport';

export default MarketplaceViewport;
